import { Card, Stack } from '@mui/material';
import { ShowIf } from '@/utils';
import React from 'react';
import { BreadcrumbsLinkProps } from '@/components/layout/custom-breadcrumbs';
import { SplashScreen, LoadingScreen } from '@/components/feedback/loading-screen';
import TableActions from '@/components/data-display/custom-table/table-actions';
import TableContent from '@/components/data-display/custom-table/table-content';
import TablePaginationWrapper from '@/components/data-display/custom-table/table-pagination-wrapper';
import CustomScreen from './custom-screen';

interface ICustomScreenListProps {
  heading: string;
  links: BreadcrumbsLinkProps[];
  isLoading: boolean;
  action?: React.ReactNode;
  children?: React.ReactNode;
  options?: React.ReactNode | ((popover: any) => React.ReactNode);
  tableHeaders?: any[];
  tableFilters?: React.ReactNode;
  rowRenderer?: React.ReactNode;
  results?: React.ReactNode;
  filters?: any;
  data?: any;
  rowsPerPage?: number;
  page?: number;
  handleChangePage?: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilterSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilters?: (key: string, value: any) => void;
  handleSortRequest?: (column: string) => void;
  noContainer?: boolean; // When true, renders without Container and breadcrumbs
  standalone?: boolean; // When true, renders only content without any wrapper
  // Enhanced props for better filter support
  filterFields?: any[]; // For FiltersHub
  onResetFilters?: () => void; // For reset functionality
  canReset?: boolean; // To determine if reset is available
  tabs?: React.ReactNode; // For tab navigation
  statusTabs?: React.ReactNode; // For status filter tabs (rendered inside card)
  onlyRenderContent?: boolean; // If true, only renders the content without any additional layout
  // Spacing customization props
  containerSx?: any; // Custom styles for the main container
  cardSx?: any; // Custom styles for the main card
  alertBanner?: React.ReactNode; // Alert banner to display above the table
}
export default function CustomScreenList({
  heading,
  links,
  action,
  isLoading,
  children,
  options,
  tableFilters,
  results,
  tableHeaders,
  rowRenderer,
  filters,
  handleChangePage,
  handleChangeRowsPerPage,
  rowsPerPage,
  page,
  data,
  handleFilterSearch,
  handleFilters,
  handleSortRequest,
  noContainer = false,
  standalone = false,
  onlyRenderContent = false,
  tabs,
  statusTabs,
  filterFields,
  onResetFilters,
  canReset,
  containerSx,
  cardSx,
  alertBanner,
}: ICustomScreenListProps) {
  if (onlyRenderContent) {
    return children;
  }

  const renderContent = (): React.ReactNode => (
    <>
      {alertBanner}
      {statusTabs}
      <TableActions
        filters={filters}
        tableFilters={tableFilters}
        options={options}
        handleFilterSearch={handleFilterSearch}
        handleFilters={handleFilters}
        filterFields={filterFields}
        onResetFilters={onResetFilters}
        canReset={canReset}
      />
      {results}
      <TableContent
        tableHeaders={tableHeaders}
        rowRenderer={rowRenderer}
        handleSortRequest={handleSortRequest}
        filters={filters}
      />
      <TablePaginationWrapper
        data={data}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
      {children}
    </>
  );

  // If standalone is true, render only the content without any wrapper
  if (standalone) {
    return (
      <ShowIf condition={!!isLoading} alternative={renderContent()}>
        <SplashScreen sx={{ bgcolor: 'transparent' }} />
      </ShowIf>
    );
  }

  // If noContainer is true, just render the card content without container and breadcrumbs
  if (noContainer) {
    return (
      <Card
        sx={{
          overflow: 'hidden',
          ...cardSx,
        }}
      >
        <ShowIf condition={!!isLoading} alternative={renderContent()}>
          <SplashScreen sx={{ bgcolor: 'transparent' }} />
        </ShowIf>
      </Card>
    );
  }

  return (
    <CustomScreen
      containerSx={containerSx}
      links={links}
      heading={heading}
      action={action}
      isLoading={isLoading}
      tabs={tabs}
    >
      <Card
        sx={{
          overflow: 'hidden',
          ...cardSx,
        }}
      >
        <ShowIf condition={!!isLoading} alternative={renderContent()}>
          <SplashScreen sx={{ bgcolor: 'transparent' }} />
        </ShowIf>
      </Card>
    </CustomScreen>
  );
}
